import React from 'react';
import { formatEnumValue } from 'formatters';
import { ArrowRightAlt } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

interface FormRule {
  fact: string;
  minValue?: string;
  maxValue?: string;
  requiredForQuote?: boolean;
}

type CriteriaSnapshot = Record<string, FormRule[]>;

interface DiffValueProps {
  before: string;
  after: string;
}

const DiffValue = ({ before, after }: DiffValueProps) => {
  const changed = before !== after;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography
        color={changed ? 'error.main' : 'text.primary'}
        sx={{ textDecoration: changed ? 'line-through' : 'none' }}
      >
        {before}
      </Typography>
      {changed && (
        <>
          <ArrowRightAlt fontSize="small" />
          <Typography color="success.main">{after}</Typography>
        </>
      )}
    </Stack>
  );
};

const formatRange = (rule: FormRule | undefined): string => {
  if (!rule) return '—';
  return `${rule.minValue ?? '—'} – ${rule.maxValue ?? '—'}`;
};

interface TermDiffProps {
  termKey: string;
  before: FormRule[] | undefined;
  after: FormRule[] | undefined;
}

const TermDiff = ({ termKey, before, after }: TermDiffProps) => {
  const bRules = before ?? [];
  const aRules = after ?? [];

  const allFacts = Array.from(new Set([...bRules.map(r => r.fact), ...aRules.map(r => r.fact)]));

  const changedFacts = allFacts.filter(fact => {
    const b = bRules.find(r => r.fact === fact);
    const a = aRules.find(r => r.fact === fact);
    return formatRange(b) !== formatRange(a);
  });

  if (!changedFacts.length) return null;

  return (
    <Stack spacing={1}>
      <Typography>Configuration {Number(termKey) + 1}</Typography>

      <Stack gap="0.2rem" mt={0.5}>
        {changedFacts.map(fact => {
          const b = bRules.find(r => r.fact === fact);
          const a = aRules.find(r => r.fact === fact);

          return (
            <Stack key={fact} direction="row" alignItems="center" gap="0.5rem">
              <Typography>{formatEnumValue(fact)}:</Typography>
              <DiffValue before={formatRange(b)} after={formatRange(a)} />
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

export interface CriteriaRenderProps {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export const CriteriaRender = ({ before, after }: CriteriaRenderProps) => {
  const bMap = (before ?? {}) as CriteriaSnapshot;
  const aMap = (after ?? {}) as CriteriaSnapshot;
  const keys = Array.from(new Set([...Object.keys(bMap), ...Object.keys(aMap)]));

  return (
    <Stack spacing={1}>
      {keys.map(key => (
        <TermDiff key={key} termKey={key} before={bMap[key]} after={aMap[key]} />
      ))}
    </Stack>
  );
};
